// src/pages/PublicLandingPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
   Box,
   Container,
   Typography,
   Button,
   Card,
   CardContent,
   CircularProgress,
   Avatar,
   Paper,
   Grid,
} from '@mui/material';
import {
   LocationOn as MapPin,
   Event as Calendar,
   Image as ImageIcon,
   OpenInNew as ExternalLink,
   AttachMoney as DollarSign,
   ArrowForward as ArrowRight,
   Language as Globe,
   Email as Mail,
   PersonAdd as UserPlus,
   Person as User,
} from '@mui/icons-material';
import type {
   LandingPageSettings,
   Album,
   AlbumPhoto,
   EventItem,
   TripItem,
   ExternalLink as ExternalLinkType,
} from '../types/LandingPage';

const PublicLandingPage: React.FC = () => {
   const { userId, username } = useParams<{ userId: string; username: string }>();
   const navigate = useNavigate();

   const [settings, setSettings] = useState<LandingPageSettings | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(false);

   const [selectedEvents, setSelectedEvents] = useState<EventItem[]>([]);
   const [selectedTrips, setSelectedTrips] = useState<TripItem[]>([]);
   const [selectedAlbums, setSelectedAlbums] = useState<Album[]>([]);
   const [albumPhotoCounts, setAlbumPhotoCounts] = useState<{ [key: string]: number }>({});

   useEffect(() => {
      loadLandingPage();
   }, [userId, username]);

   const loadLandingPage = async () => {
      try {
         setLoading(true);

         // Load settings using userId
         const settingsRes = await fetch(
            `${process.env.REACT_APP_API_URL}/api/landing-page-settings?userId=${userId}`
         );

         if (!settingsRes.ok) {
            setError(true);
            return;
         }

         const settingsData: LandingPageSettings = await settingsRes.json();
         console.log('Landing page settings loaded:', settingsData);
         console.log('User location:', settingsData.userLocation);
         console.log('User email:', settingsData.userEmail);
         console.log('Custom bio:', settingsData.customBio);

         // Verify that the username in URL matches the settings
         if (settingsData.username !== username) {
            setError(true);
            return;
         }

         setSettings(settingsData);

         // Load events if enabled
         if (settingsData.showUpcomingEvents && settingsData.selectedEventIds) {
            const eventsRes = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events?userId=${settingsData.userId}`
            );
            if (eventsRes.ok) {
               const allEvents: EventItem[] = await eventsRes.json();
               console.log('All events loaded:', allEvents);
               console.log('Selected event IDs:', settingsData.selectedEventIds);
               const filtered = allEvents.filter((event) =>
                  settingsData.selectedEventIds?.includes(event.id)
               );
               console.log('Filtered events:', filtered);
               setSelectedEvents(filtered);
            }
         }

         // Load trips if enabled
         if (settingsData.showTrips && settingsData.selectedTripIds) {
            const tripsRes = await fetch(
               `${process.env.REACT_APP_API_URL}/api/trips?userId=${settingsData.userId}`
            );
            if (tripsRes.ok) {
               const allTrips: TripItem[] = await tripsRes.json();
               const filtered = allTrips.filter((trip) =>
                  settingsData.selectedTripIds?.includes(trip.id)
               );
               setSelectedTrips(filtered);
            }
         }

         // Load albums if enabled
         if (settingsData.showPhotoAlbums && settingsData.selectedAlbumIds) {
            const albumsRes = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums?userId=${settingsData.userId}`
            );
            if (albumsRes.ok) {
               const allAlbums: Album[] = await albumsRes.json();
               const filtered = allAlbums.filter((album) =>
                  settingsData.selectedAlbumIds?.includes(album.id)
               );
               setSelectedAlbums(filtered);

               // Load photo counts for each album
               const counts: { [key: string]: number } = {};
               for (const album of filtered) {
                  const photosRes = await fetch(
                     `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/photos`
                  );
                  if (photosRes.ok) {
                     const photos: AlbumPhoto[] = await photosRes.json();
                     counts[album.id] = photos.length;
                  }
               }
               setAlbumPhotoCounts(counts);
            }
         }
      } catch (err) {
         console.error('Error loading landing page:', err);
         setError(true);
      } finally {
         setLoading(false);
      }
   };

   const externalLinks: ExternalLinkType[] = settings?.externalLinks
      ? settings.externalLinks.map((link) => JSON.parse(link))
      : [];

   const defaultHeroPhoto = 'https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&q=80';

   if (loading) {
      return (
         <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
            <Box textAlign='center'>
               <CircularProgress size={48} sx={{ mb: 2 }} />
               <Typography color='text.secondary'>Loading...</Typography>
            </Box>
         </Box>
      );
   }

   if (error || !settings) {
      return (
         <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh' p={3}>
            <Box textAlign='center' maxWidth={500}>
               <Typography variant='h3' fontWeight='bold' gutterBottom>
                  404
               </Typography>
               <Typography variant='h6' color='text.secondary' gutterBottom>
                  Profile not found
               </Typography>
               <Button variant='contained' onClick={() => navigate('/')} sx={{ mt: 3 }}>
                  Go Home
               </Button>
            </Box>
         </Box>
      );
   }

   return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
         {/* Hero Section */}
         <Box
            sx={{
               position: 'relative',
               height: '800px',
               overflow: 'hidden',
            }}
         >
            {/* Hero Photo Background */}
            <Box
               component='img'
               src={settings.heroPhoto || defaultHeroPhoto}
               alt={`${username}'s profile`}
               sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
               }}
            />

            {/* Dark gradient overlay */}
            <Box
               sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                     'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.5), rgba(0,0,0,0.7))',
               }}
            />

            {/* Content */}
            <Box
               sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 3,
                  textAlign: 'center',
               }}
            >
               {/* Profile Photo */}
               {settings.profilePhoto ? (
                  <Avatar
                     src={settings.profilePhoto}
                     alt={username}
                     sx={{
                        width: { xs: 120, md: 140 },
                        height: { xs: 120, md: 140 },
                        border: '6px solid white',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                        mb: 3,
                     }}
                  />
               ) : (
                  <Avatar
                     sx={{
                        width: { xs: 120, md: 140 },
                        height: { xs: 120, md: 140 },
                        border: '6px solid white',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                        mb: 3,
                        bgcolor: 'primary.main',
                     }}
                  >
                     <User sx={{ fontSize: 70, color: 'white' }} />
                  </Avatar>
               )}

               {/* Name */}
               <Typography
                  sx={{
                     mb: 2,
                     fontWeight: 300,
                     fontSize: { xs: '3rem', sm: '4rem', md: '4.5rem' },
                     letterSpacing: '-0.02em',
                     lineHeight: 1.1,
                     color: 'white',
                     textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  }}
               >
                  {username}
               </Typography>

               {/* Tagline */}
               {settings.showProfileOverview && settings.customTagline && (
                  <Typography
                     sx={{
                        mb: 3,
                        fontWeight: 300,
                        fontSize: { xs: '1.375rem', md: '1.625rem' },
                        lineHeight: 1.5,
                        maxWidth: { xs: '90%', md: '700px' },
                        color: 'white',
                        textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        px: 2,
                     }}
                  >
                     {settings.customTagline}
                  </Typography>
               )}

               {/* Bio */}
               {settings.showProfileOverview && settings.customBio && (
                  <Typography
                     sx={{
                        mb: 4,
                        fontSize: { xs: '1rem', md: '1.0625rem' },
                        lineHeight: 1.7,
                        fontWeight: 300,
                        maxWidth: { xs: '90%', md: '750px' },
                        px: { xs: 2, md: 2 },
                        whiteSpace: 'pre-wrap',
                        color: 'rgba(255,255,255,0.95)',
                        textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                     }}
                  >
                     {settings.customBio}
                  </Typography>
               )}

               {/* Profile Link Button */}
               {settings.showProfileOverview && settings.showProfileLink && (
                  <Button
                     variant='contained'
                     startIcon={<User sx={{ fontSize: 18 }} />}
                     onClick={() => navigate('/profile')}
                     sx={{
                        bgcolor: '#14b8a6',
                        color: 'white',
                        px: 3.5,
                        py: 1.25,
                        fontSize: '0.9375rem',
                        fontWeight: 400,
                        textTransform: 'none',
                        borderRadius: '6px',
                        boxShadow: 'none',
                        '&:hover': {
                           bgcolor: '#14b8a6',
                           boxShadow: 'none',
                        },
                     }}
                  >
                     View Full Profile
                  </Button>
               )}
            </Box>

            {/* Scroll indicator */}
            <Box
               sx={{
                  position: 'absolute',
                  bottom: 32,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  animation: 'bounce 2s infinite',
                  '@keyframes bounce': {
                     '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
                     '50%': { transform: 'translateX(-50%) translateY(-10px)' },
                  },
               }}
            >
               <Box
                  sx={{
                     width: 24,
                     height: 40,
                     border: '2px solid rgba(255,255,255,0.6)',
                     borderRadius: '12px',
                     display: 'flex',
                     alignItems: 'flex-start',
                     justifyContent: 'center',
                     p: 1,
                  }}
               >
                  <Box
                     sx={{
                        width: 6,
                        height: 6,
                        bgcolor: 'rgba(255,255,255,0.6)',
                        borderRadius: '50%',
                     }}
                  />
               </Box>
            </Box>
         </Box>

         {/* Main Content */}
         <Container maxWidth='lg' sx={{ py: 6 }}>
            <Box
               sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  maxWidth: '900px',
                  mx: 'auto',
                  width: '100%',
               }}
            >
               {/* Profile Overview */}
               {settings.showProfileOverview && (settings.userLocation || settings.userEmail) && (
                  <Paper sx={{ p: 4, borderRadius: 2, width: '100%' }}>
                     <Typography variant='h5' fontWeight='bold' gutterBottom>
                        Contact Info
                     </Typography>
                     <Grid container spacing={3} sx={{ mt: 2 }}>
                        {settings.userLocation && (
                           <Grid item xs={12} md={6}>
                              <Box display='flex' alignItems='center' gap={2}>
                                 <Box
                                    sx={{
                                       width: 40,
                                       height: 40,
                                       borderRadius: '50%',
                                       bgcolor: 'primary.light',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                    }}
                                 >
                                    <MapPin color='primary' />
                                 </Box>
                                 <Box>
                                    <Typography variant='body2' color='text.secondary'>
                                       Location
                                    </Typography>
                                    <Typography fontWeight='500'>
                                       {settings.userLocation}
                                    </Typography>
                                 </Box>
                              </Box>
                           </Grid>
                        )}
                        {settings.userEmail && (
                           <Grid item xs={12} md={6}>
                              <Box display='flex' alignItems='center' gap={2}>
                                 <Box
                                    sx={{
                                       width: 40,
                                       height: 40,
                                       borderRadius: '50%',
                                       bgcolor: 'primary.light',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                    }}
                                 >
                                    <Mail color='primary' />
                                 </Box>
                                 <Box>
                                    <Typography variant='body2' color='text.secondary'>
                                       Email
                                    </Typography>
                                    <Typography fontWeight='500'>{settings.userEmail}</Typography>
                                 </Box>
                              </Box>
                           </Grid>
                        )}
                     </Grid>
                  </Paper>
               )}

               {/* Upcoming Events */}
               {settings.showUpcomingEvents && selectedEvents.length > 0 && (
                  <Paper sx={{ p: 4, borderRadius: 2, width: '100%' }}>
                     <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
                        <Typography variant='h5' fontWeight='bold'>
                           Upcoming Events
                        </Typography>
                        <Calendar color='primary' />
                     </Box>
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {selectedEvents.map((event) => (
                           <Card key={event.id} sx={{ bgcolor: 'grey.50' }}>
                              <CardContent>
                                 <Box display='flex' alignItems='flex-start' gap={2}>
                                    {event.imageUrl && (
                                       <Box
                                          component='img'
                                          src={event.imageUrl}
                                          alt={event.title}
                                          sx={{
                                             width: 96,
                                             height: 96,
                                             borderRadius: 2,
                                             objectFit: 'cover',
                                             flexShrink: 0,
                                          }}
                                       />
                                    )}
                                    <Box flex={1}>
                                       <Typography variant='h6' fontWeight='600' gutterBottom>
                                          {event.title}
                                       </Typography>
                                       {event.description && (
                                          <Typography
                                             variant='body2'
                                             color='text.secondary'
                                             gutterBottom
                                          >
                                             {event.description}
                                          </Typography>
                                       )}
                                       <Box display='flex' gap={2} mt={1}>
                                          <Box display='flex' alignItems='center' gap={0.5}>
                                             <Calendar sx={{ fontSize: 16 }} />
                                             <Typography variant='body2' color='text.secondary'>
                                                {new Date(event.date).toLocaleDateString()}
                                             </Typography>
                                          </Box>
                                          <Box display='flex' alignItems='center' gap={0.5}>
                                             <MapPin sx={{ fontSize: 16 }} />
                                             <Typography variant='body2' color='text.secondary'>
                                                {event.location}
                                             </Typography>
                                          </Box>
                                       </Box>
                                    </Box>
                                 </Box>
                              </CardContent>
                           </Card>
                        ))}
                     </Box>
                  </Paper>
               )}

               {/* Trips */}
               {settings.showTrips && selectedTrips.length > 0 && (
                  <Paper sx={{ p: 4, borderRadius: 2, width: '100%' }}>
                     <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
                        <Typography variant='h5' fontWeight='bold'>
                           Upcoming Trips
                        </Typography>
                        <MapPin color='primary' />
                     </Box>
                     <Grid container spacing={3}>
                        {selectedTrips.map((trip) => (
                           <Grid item xs={12} md={6} key={trip.id}>
                              <Card
                                 sx={{
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': {
                                       borderColor: 'primary.main',
                                       '& img': { transform: 'scale(1.05)' },
                                    },
                                    transition: 'all 0.3s',
                                 }}
                              >
                                 {trip.imageUrl && (
                                    <Box
                                       sx={{
                                          aspectRatio: '16/9',
                                          overflow: 'hidden',
                                          bgcolor: 'grey.100',
                                       }}
                                    >
                                       <Box
                                          component='img'
                                          src={trip.imageUrl}
                                          alt={trip.title}
                                          sx={{
                                             width: '100%',
                                             height: '100%',
                                             objectFit: 'cover',
                                             transition: 'transform 0.3s',
                                          }}
                                       />
                                    </Box>
                                 )}
                                 <CardContent>
                                    <Typography variant='h6' fontWeight='600' gutterBottom>
                                       {trip.title}
                                    </Typography>
                                    {trip.description && (
                                       <Typography
                                          variant='body2'
                                          color='text.secondary'
                                          gutterBottom
                                       >
                                          {trip.description}
                                       </Typography>
                                    )}
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: 1,
                                          mt: 2,
                                       }}
                                    >
                                       <Box display='flex' alignItems='center' gap={0.5}>
                                          <Calendar sx={{ fontSize: 16 }} />
                                          <Typography variant='body2' color='text.secondary'>
                                             {new Date(trip.startDate).toLocaleDateString()} -{' '}
                                             {new Date(trip.endDate).toLocaleDateString()}
                                          </Typography>
                                       </Box>
                                       <Box display='flex' alignItems='center' gap={0.5}>
                                          <MapPin sx={{ fontSize: 16 }} />
                                          <Typography variant='body2' color='text.secondary'>
                                             {trip.location}
                                          </Typography>
                                       </Box>
                                    </Box>
                                 </CardContent>
                              </Card>
                           </Grid>
                        ))}
                     </Grid>
                  </Paper>
               )}

               {/* Photo Albums */}
               {settings.showPhotoAlbums && selectedAlbums.length > 0 && (
                  <Paper sx={{ p: 4, borderRadius: 2, width: '100%' }}>
                     <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
                        <Typography variant='h5' fontWeight='bold'>
                           Photo Albums
                        </Typography>
                        <ImageIcon color='primary' />
                     </Box>
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {selectedAlbums.map((album) => {
                           const photoCount = albumPhotoCounts[album.id] || 0;

                           return (
                              <Card key={album.id} sx={{ bgcolor: 'grey.50' }}>
                                 <CardContent>
                                    <Box display='flex' alignItems='flex-start' gap={2}>
                                       {album.coverPhoto ? (
                                          <Box
                                             component='img'
                                             src={album.coverPhoto}
                                             alt={album.title}
                                             sx={{
                                                width: 96,
                                                height: 96,
                                                borderRadius: 2,
                                                objectFit: 'cover',
                                                flexShrink: 0,
                                             }}
                                          />
                                       ) : (
                                          <Box
                                             sx={{
                                                width: 96,
                                                height: 96,
                                                borderRadius: 2,
                                                bgcolor: 'grey.200',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                             }}
                                          >
                                             <ImageIcon
                                                sx={{ fontSize: 40, color: 'text.disabled' }}
                                             />
                                          </Box>
                                       )}
                                       <Box flex={1}>
                                          <Typography variant='h6' fontWeight='600' gutterBottom>
                                             {album.title}
                                          </Typography>
                                          {album.description && (
                                             <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                gutterBottom
                                                sx={{
                                                   display: '-webkit-box',
                                                   WebkitLineClamp: 2,
                                                   WebkitBoxOrient: 'vertical',
                                                   overflow: 'hidden',
                                                }}
                                             >
                                                {album.description}
                                             </Typography>
                                          )}
                                          <Box display='flex' gap={2} mt={1} flexWrap='wrap'>
                                             {album.date && (
                                                <Box display='flex' alignItems='center' gap={0.5}>
                                                   <Calendar sx={{ fontSize: 16 }} />
                                                   <Typography
                                                      variant='body2'
                                                      color='text.secondary'
                                                   >
                                                      {new Date(album.date).toLocaleDateString()}
                                                   </Typography>
                                                </Box>
                                             )}
                                             {album.location && (
                                                <Box display='flex' alignItems='center' gap={0.5}>
                                                   <MapPin sx={{ fontSize: 16 }} />
                                                   <Typography
                                                      variant='body2'
                                                      color='text.secondary'
                                                   >
                                                      {album.location}
                                                   </Typography>
                                                </Box>
                                             )}
                                             <Box display='flex' alignItems='center' gap={0.5}>
                                                <ImageIcon sx={{ fontSize: 16 }} />
                                                <Typography variant='body2' color='text.secondary'>
                                                   {photoCount}{' '}
                                                   {photoCount === 1 ? 'photo' : 'photos'}
                                                </Typography>
                                             </Box>
                                          </Box>
                                       </Box>
                                    </Box>
                                 </CardContent>
                              </Card>
                           );
                        })}
                     </Box>
                  </Paper>
               )}

               {/* External Links */}
               {settings.showExternalLinks && externalLinks.length > 0 && (
                  <Paper sx={{ p: 4, borderRadius: 2, width: '100%' }}>
                     <Typography variant='h5' fontWeight='bold' gutterBottom>
                        Connect With Me
                     </Typography>
                     <Grid container spacing={2} sx={{ mt: 1 }}>
                        {externalLinks.map((link, index) => (
                           <Grid item xs={12} sm={6} md={4} key={index}>
                              <Button
                                 component='a'
                                 href={link.url}
                                 target='_blank'
                                 rel='noopener noreferrer'
                                 fullWidth
                                 sx={{
                                    p: 2,
                                    bgcolor: 'grey.50',
                                    color: 'text.primary',
                                    textTransform: 'none',
                                    justifyContent: 'space-between',
                                    '&:hover': { bgcolor: 'grey.100' },
                                 }}
                              >
                                 <Box display='flex' alignItems='center' gap={2}>
                                    <Box
                                       sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: '50%',
                                          bgcolor: 'primary.light',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                       }}
                                    >
                                       <Globe color='primary' />
                                    </Box>
                                    <Typography fontWeight='500'>{link.title}</Typography>
                                 </Box>
                                 <ExternalLink sx={{ color: 'text.secondary' }} />
                              </Button>
                           </Grid>
                        ))}
                     </Grid>
                  </Paper>
               )}

               {/* Booking Button */}
               {settings.showBookingButton && settings.bookingPrice && (
                  <Paper
                     sx={{
                        p: 4,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                        color: 'white',
                        textAlign: 'center',
                        width: '100%',
                     }}
                  >
                     <Typography variant='h4' fontWeight='bold' gutterBottom>
                        Book a Call
                     </Typography>
                     <Typography variant='body1' sx={{ mb: 3, opacity: 0.9 }}>
                        Schedule a consultation session
                     </Typography>
                     <Box display='flex' alignItems='center' justifyContent='center' gap={1} mb={3}>
                        <DollarSign sx={{ fontSize: 32 }} />
                        <Typography variant='h3' fontWeight='bold'>
                           {settings.bookingPrice}
                        </Typography>
                        <Typography variant='h6'>{settings.bookingCurrency}</Typography>
                        <Typography variant='body2' sx={{ opacity: 0.7 }}>
                           / session
                        </Typography>
                     </Box>
                     <Button
                        variant='contained'
                        size='large'
                        endIcon={<ArrowRight />}
                        sx={{
                           bgcolor: 'white',
                           color: 'primary.main',
                           '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                     >
                        Book Now
                     </Button>
                  </Paper>
               )}

               {/* WeTrippen Connect */}
               <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center', width: '100%' }}>
                  <Typography variant='h5' fontWeight='bold' gutterBottom>
                     WeTrippen Connect
                  </Typography>
                  <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
                     Send a connection request to join {username}'s network
                  </Typography>
                  <Button variant='contained' size='large' startIcon={<UserPlus />}>
                     Send Connection Request
                  </Button>
               </Paper>
            </Box>
         </Container>

         {/* Footer */}
         <Box sx={{ borderTop: '1px solid', borderColor: 'divider', py: 4 }}>
            <Container maxWidth='lg'>
               <Typography variant='body2' color='text.secondary' textAlign='center'>
                  Powered by WeTrippen
               </Typography>
            </Container>
         </Box>
      </Box>
   );
};

export default PublicLandingPage;
